import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  orderBy,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Quote, QuoteDraft } from '@/types/Quote';

function quotesCollection(uid: string) {
  return collection(db, 'users', uid, 'quotes');
}

/**
 * Returns the next quote number for this user, formatted SWC-YYYY-NNNN.
 * Phase 1 uses a count-based approach (race-tolerant for single-user
 * scenarios). Phase 2 will use a transactional counter doc to guarantee
 * sequential uniqueness under concurrent saves.
 */
export async function nextQuoteNumber(uid: string): Promise<string> {
  const year = new Date().getFullYear();
  const yearStart = Timestamp.fromDate(new Date(year, 0, 1));
  const yearEnd = Timestamp.fromDate(new Date(year + 1, 0, 1));
  const q = query(
    quotesCollection(uid),
    where('createdAt', '>=', yearStart),
    where('createdAt', '<', yearEnd)
  );
  const snap = await getDocs(q);
  const seq = String(snap.size + 1).padStart(4, '0');
  return `SWC-${year}-${seq}`;
}

export async function saveQuote(draft: QuoteDraft): Promise<string> {
  const ref = doc(quotesCollection(draft.userId), draft.id);
  await setDoc(ref, {
    ...draft,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return draft.id;
}

export async function listUserQuotes(uid: string): Promise<Quote[]> {
  const snap = await getDocs(
    query(quotesCollection(uid), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Quote));
}

export function newQuoteId(): string {
  // Firestore-style 20-char auto-id. Good enough for Phase 1.
  const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 20; i++) {
    id += alpha[Math.floor(Math.random() * alpha.length)];
  }
  return id;
}
